import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { isFuture } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Info } from '@sorare/core/src/atoms/icons/Info';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, Caption, LabelS } from '@sorare/core/src/atoms/typography';
import { NoCardEntryInfoDialog } from '@sorare/core/src/components/noCardEntry/NoCardEntryInfoDialog';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import TimeLeft from '@sorare/core/src/contexts/ticker/TimeLeft';
import useNoCardRoute from '@sorare/core/src/hooks/config/useNoCardRoute';
import { glossary } from '@sorare/core/src/lib/glossary';

import So5NoCardEntryRegisterDialog from 'components/noCardEntry/So5NoCardEntryRegisterDialog';

const Root = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background-color: var(--c-neutral-200);

  > button {
    align-self: start;
  }
`;

const EXAMPLE_SO5_GAME_WEEK_POOL_COMPOSITION = {
  gameWeek: 377,
  limited: 780617,
  rare: 256231,
  superRare: 26141,
  unique: 2582,
};

export const NoCardEntry = () => {
  const { currentUser } = useCurrentUserContext();
  const {
    noCardRoute: { nextOpenDate, nextCloseDate, emailAddress },
    loading,
  } = useNoCardRoute();

  const [mainDialogOpened, setMainDialogOpened] = useState(false);
  const [infoDialogOpened, setInfoDialogOpened] = useState(false);

  if (!currentUser?.noCardRouteEnabled) return null;

  const { so5NoCardRouteOpened } = currentUser;
  const showOpensIn = !!nextOpenDate && isFuture(nextOpenDate);
  const showCloseIn =
    !showOpensIn && !!nextCloseDate && isFuture(nextCloseDate);

  return (
    <Root>
      <Vertical gap={0}>
        <SBHorizontal>
          <BodyM as="p" bold>
            <FormattedMessage {...glossary.noCardEntry} />
          </BodyM>
          <Caption
            as="button"
            onClick={() => setInfoDialogOpened(true)}
            type="button"
          >
            <Info />
          </Caption>
        </SBHorizontal>
        <LabelS as="p" color="var(--c-neutral-500)">
          {showOpensIn || showCloseIn ? (
            <Horizontal as="span">
              <FontAwesomeIcon icon={faClock} />
              <span>
                {showOpensIn && (
                  <FormattedMessage
                    {...glossary.opensIn}
                    values={{ time: <TimeLeft time={nextOpenDate} /> }}
                  />
                )}
                {showCloseIn && (
                  <FormattedMessage
                    {...glossary.closesIn}
                    values={{ time: <TimeLeft time={nextCloseDate} /> }}
                  />
                )}
              </span>
            </Horizontal>
          ) : (
            <>
              {loading ? (
                <>&nbsp;</>
              ) : (
                <FormattedMessage {...glossary.closed} />
              )}
            </>
          )}
        </LabelS>
      </Vertical>
      <Button
        size="medium"
        color="tertiary"
        disabled={!so5NoCardRouteOpened}
        onClick={() => setMainDialogOpened(true)}
      >
        <FormattedMessage {...glossary.register} />
      </Button>
      <So5NoCardEntryRegisterDialog
        open={mainDialogOpened}
        onClose={() => setMainDialogOpened(false)}
      />
      <NoCardEntryInfoDialog
        open={infoDialogOpened}
        onClose={() => setInfoDialogOpened(false)}
        emailAddress={emailAddress}
        poolDetail={
          <FormattedMessage
            id="Lobby.CompetitionList.NoCardEntry.poolDetail"
            defaultMessage="For instance, for Game Week {gameWeek}, the pool of players available was composed of {limitedCount} Limited Cards, {rareCount} Rare Cards, {superRareCount} Super Rare Cards, {uniqueCount} Unique Cards."
            values={{
              gameWeek: EXAMPLE_SO5_GAME_WEEK_POOL_COMPOSITION.gameWeek,
              limitedCount: EXAMPLE_SO5_GAME_WEEK_POOL_COMPOSITION.limited,
              rareCount: EXAMPLE_SO5_GAME_WEEK_POOL_COMPOSITION.rare,
              superRareCount: EXAMPLE_SO5_GAME_WEEK_POOL_COMPOSITION.superRare,
              uniqueCount: EXAMPLE_SO5_GAME_WEEK_POOL_COMPOSITION.unique,
            }}
          />
        }
      />
    </Root>
  );
};
